import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/authService';
import { Candidate } from '../types';
import { colors } from '../styles/colors';

interface CandidateDetailProps {
  candidateId: number;
  onClose: () => void;
}

const CandidateDetail: React.FC<CandidateDetailProps> = ({ candidateId, onClose }) => {
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState<number[][]>([]);
  const tramposBaseUrl = typeof window !== 'undefined' && window.location.hostname.includes('staging')
    ? 'https://staging.trampos.co'
    : 'https://trampos.co';

  useEffect(() => {
    fetchCandidate();
    fetchChartData();
  }, [candidateId]);

  const fetchCandidate = async () => {
    try {
      const response = await api.get(`/candidates/${candidateId}`);
      setCandidate(response.data);
    } catch (error: any) {
      toast.error('Erro ao carregar candidato');
    } finally {
      setLoading(false);
    }
  };

  const fetchChartData = async () => {
    try {
      const response = await api.get(`/candidates/${candidateId}/applies-chart`);
      setChartData(response.data.data);
    } catch (error: any) {
      console.error('Erro ao carregar dados do gráfico:', error);
    }
  };

  if (loading) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.loading}>Carregando...</div>
        </div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div style={styles.overlay}>
        <div style={styles.modal}>
          <div style={styles.error}>Candidato não encontrado</div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        <div style={styles.header}>
          <h2 style={styles.title}>
            Detalhes do Candidato {candidate.deleted_at && '(excluído)'}
          </h2>
          <button onClick={onClose} style={styles.closeBtn}>×</button>
        </div>

        <div style={styles.content}>
          {/* Painel Conta */}
          <div style={styles.panel}>
            <h3 style={styles.panelTitle}>Conta</h3>
            <div style={styles.panelContent}>
              <div style={styles.row}>
                <strong>Email:</strong> {candidate.email}
              </div>
              <div style={styles.row}>
                <strong>CPF:</strong> {candidate.document_number || '-'}
              </div>
              <div style={styles.row}>
                <strong>Nome:</strong> {candidate.name || '-'}
              </div>
              <div style={styles.row}>
                <strong>Sobrenome:</strong> {candidate.lastname || '-'}
              </div>
              <div style={styles.row}>
                <strong>Data de Nascimento:</strong> {
                  candidate.birthdate 
                    ? new Date(candidate.birthdate).toLocaleDateString('pt-BR')
                    : '-'
                }
              </div>
              <div style={styles.row}>
                <strong>Cidade:</strong> {candidate.candidateProfile?.city || '-'}
              </div>
            </div>
          </div>

          {/* Painel Perfil */}
          {candidate.candidateProfile && (
            <div style={styles.panel}>
              <h3 style={styles.panelTitle}>Endereço</h3>
              <div style={styles.panelContent}>
                <div style={styles.row}>
                  <strong>Rua:</strong> {candidate.candidateProfile.street || '-'}
                </div>
                <div style={styles.row}>
                  <strong>Número:</strong> {candidate.candidateProfile.number || '-'}
                </div>
                <div style={styles.row}>
                  <strong>Complemento:</strong> {candidate.candidateProfile.complement || '-'}
                </div>
                <div style={styles.row}>
                  <strong>CEP:</strong> {candidate.candidateProfile.zip || '-'}
                </div>
                <div style={styles.row}>
                  <strong>Bairro:</strong> {candidate.candidateProfile.neighborhood || '-'}
                </div>
                <div style={styles.row}>
                  <strong>Cidade:</strong> {candidate.candidateProfile.city || '-'}
                </div>
                <div style={styles.row}>
                  <strong>Estado:</strong> {candidate.candidateProfile.state || '-'}
                </div>
              </div>
            </div>
          )}

          {/* Painel Estatísticas */}
          <div style={styles.panel}>
            <h3 style={styles.panelTitle}>Estatísticas</h3>
            <div style={styles.panelContent}>
              <div style={styles.row}>
                <strong>Status:</strong> {
                  candidate.is_premium ? (
                    <span style={styles.premiumBadge}>PREMIUM</span>
                  ) : (
                    <span>Regular</span>
                  )
                }
              </div>
              <div style={styles.row}>
                <strong>Total de Candidaturas:</strong> {candidate.applies_count || 0}
              </div>
              <div style={styles.row}>
                <strong>Stamina:</strong> {candidate.stamina || 0}
              </div>
              <div style={styles.row}>
                <strong>Perfil Visível:</strong> {candidate.search_opt_in ? 'Sim' : 'Não'}
              </div>
              <div style={styles.row}>
                <strong>Candidato Beta:</strong> {candidate.beta ? 'Sim' : 'Não'}
              </div>
              {candidate.slug && (
                <div style={styles.row}>
                  <strong>Slug:</strong> 
                  <a 
                    href={`${tramposBaseUrl}/${candidate.slug.name}`}
                    target="_blank" 
                    rel="noopener noreferrer"
                    style={styles.link}
                  >
                    /{candidate.slug.name}
                  </a>
                </div>
              )}
            </div>
          </div>

          {/* Painel UTM */}
          <div style={styles.panel}>
            <h3 style={styles.panelTitle}>Tracking UTM</h3>
            <div style={styles.panelContent}>
              <div style={styles.row}>
                <strong>UTM Source:</strong> {candidate.utm_source || '-'}
              </div>
              <div style={styles.row}>
                <strong>UTM Medium:</strong> {candidate.utm_medium || '-'}
              </div>
              <div style={styles.row}>
                <strong>UTM Campaign:</strong> {candidate.utm_campaign || '-'}
              </div>
              <div style={styles.row}>
                <strong>UTM Content:</strong> {candidate.utm_content || '-'}
              </div>
            </div>
          </div>

          {/* Painel Datas */}
          <div style={styles.panel}>
            <h3 style={styles.panelTitle}>Datas</h3>
            <div style={styles.panelContent}>
              <div style={styles.row}>
                <strong>Criado em:</strong> {
                  new Date(candidate.created_at).toLocaleString('pt-BR')
                }
              </div>
              <div style={styles.row}>
                <strong>Atualizado em:</strong> {
                  new Date(candidate.updated_at).toLocaleString('pt-BR')
                }
              </div>
              {candidate.deleted_at && (
                <>
                  <div style={styles.row}>
                    <strong>Excluído em:</strong> {
                      new Date(candidate.deleted_at).toLocaleString('pt-BR')
                    }
                  </div>
                  <div style={styles.row}>
                    <strong>Motivo da Exclusão:</strong> {candidate.deleted_reason || 'Não informado'}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Gráfico de Candidaturas */}
          {chartData.length > 0 && (
            <div style={styles.panel}>
              <h3 style={styles.panelTitle}>Candidaturas ao Longo do Tempo</h3>
              <div style={styles.panelContent}>
                <div style={styles.chartContainer}>
                  <div style={styles.chartPlaceholder}>
                    Gráfico de candidaturas ({chartData.length} pontos de dados)
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const styles = {
  overlay: {
    position: 'fixed' as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modal: {
    backgroundColor: colors.white,
    borderRadius: '8px',
    width: '90%',
    maxWidth: '800px',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.5rem',
    borderBottom: `1px solid ${colors.border}`,
  },
  title: {
    margin: 0,
    color: colors.primary,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '2rem',
    cursor: 'pointer',
    color: colors.textLight,
  },
  content: {
    padding: '1.5rem',
  },
  panel: {
    marginBottom: '2rem',
    border: `1px solid ${colors.border}`,
    borderRadius: '4px',
  },
  panelTitle: {
    margin: 0,
    padding: '1rem',
    backgroundColor: colors.primary,
    color: colors.white,
    borderRadius: '4px 4px 0 0',
  },
  panelContent: {
    padding: '1rem',
  },
  row: {
    marginBottom: '0.5rem',
    display: 'flex',
    gap: '0.5rem',
  },
  loading: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.textLight,
  },
  error: {
    textAlign: 'center' as const,
    padding: '2rem',
    fontSize: '1.2rem',
    color: colors.error,
  },
  premiumBadge: {
    backgroundColor: colors.secondary,
    color: colors.white,
    padding: '0.25rem 0.5rem',
    borderRadius: '4px',
    fontSize: '0.8rem',
    fontWeight: 'bold',
  },
  link: {
    color: colors.primary,
    textDecoration: 'none',
  },
  chartContainer: {
    height: '200px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background,
    borderRadius: '4px',
  },
  chartPlaceholder: {
    color: colors.textLight,
    fontStyle: 'italic',
  },
};

export default CandidateDetail;
